// @ts-strict-ignore
import type { ServiceCredentials, Action } from 'flow/store/engine/types';
import { ChangeOwnerCondition } from './types';
import { CREDENTIAL_OWNER_OPTION_KEY } from 'flow/service/actionOptions';
import type { ServiceOption } from '../../types';
import { createServiceOption } from '../../../serviceOptionHelpers';


export const getCredentialKeys = (data: ServiceCredentials) => (
  [...new Set(
    [
      ...Object.keys(data.credentials),
      ...data.credential_format?.ids
        ? Object.keys(data.credential_format?.ids) : [],
      ...data.credential_format?.secrets
        ? Object.keys(data.credential_format?.secrets) : [],
    ]), CREDENTIAL_OWNER_OPTION_KEY]
);

export const buildServiceCredentials = (
  serviceCredentialsSchema: ServiceCredentials,
  actionOptions: Action['options'],
): ServiceCredentials => {
  const idsAndSecretFields = serviceCredentialsSchema?.credential_format?.ids.concat(
    serviceCredentialsSchema?.credential_format?.secrets);

  // set id and secret fields into credentials
  const credOnlyServiceData: ServiceCredentials['credentials'] = {};
  Object.entries(serviceCredentialsSchema?.credentials).forEach(([field, value]) => {
    if (idsAndSecretFields.includes(field)) {
      credOnlyServiceData[field] = value;
    }
  });

  const updatedServiceData: ServiceCredentials = {
    ...serviceCredentialsSchema,
    credentials: {
      ...serviceCredentialsSchema.credentials, ...credOnlyServiceData,
    },
  };
  const serviceDataKeys = getCredentialKeys(updatedServiceData);

  // hydrate all fields with respective action options value
  Object.entries(actionOptions).forEach(([key, actionOption]) => {
    if (serviceDataKeys.includes(key)) {
      if (key === CREDENTIAL_OWNER_OPTION_KEY) {
        updatedServiceData.credential_owner = actionOption.value as string;
      } else if (Object.hasOwn(serviceCredentialsSchema.credentials, key)) {
        updatedServiceData.credentials[key] = actionOption.value as string;
      }
    }
  });
  return updatedServiceData;
};

export const isSecretField = (
  serviceCredentials: ServiceCredentials,
  fieldName: string,
): boolean => !!serviceCredentials?.credential_format?.secrets.includes(fieldName);

export const isIdField = (
  serviceCredentials: ServiceCredentials,
  fieldName: string,
): boolean => !!serviceCredentials?.credential_format?.ids.includes(fieldName);

export const updateServiceCredentialByCondition = (
  condition: ChangeOwnerCondition,
  serviceCredentials: ServiceCredentials,
  selectedOwner: ServiceCredentials['credential_owner'],
): ServiceCredentials => {
  switch (condition) {
    case ChangeOwnerCondition.NO_OWNER:
    case ChangeOwnerCondition.CUSTOMER_AGENT_TO_CUSTOMER: {
      const currentCredentials = serviceCredentials.credentials;
      const idsAndSecretFields = [
        ...serviceCredentials.credential_format?.ids ?? [],
        ...serviceCredentials.credential_format?.secrets ?? [],
      ];

      const updatedCredentials: ServiceCredentials['credentials'] = {};
      Object.keys(currentCredentials).forEach(credentialKey => {
        if (idsAndSecretFields.includes(credentialKey)) {
          // clears credentials
          updatedCredentials[credentialKey] = '';
        } else {
          updatedCredentials[credentialKey] = currentCredentials[credentialKey];
        }
      });

      return {
        ...serviceCredentials,
        credentials: updatedCredentials,
        credential_owner: selectedOwner,
      };
    }
    case ChangeOwnerCondition.DEFAULT:
    default:
      return {
        ...serviceCredentials,
        credential_owner: selectedOwner,
      };
  }
};

export const updateOptionsByKey = (
  options: ServiceOption[],
  key: ServiceOption['key'],
  value: ServiceOption['value'],
  serviceCredentials?: ServiceCredentials,
): ServiceOption[] => {
  let updatedOptions = [...options];
  // if removing credentials, need to remove all previous set IDs/Secrets
  if (key === CREDENTIAL_OWNER_OPTION_KEY && !value) {
    const fieldsToReset = [
      ...serviceCredentials.credential_format?.ids ?? [],
      ...serviceCredentials.credential_format?.secrets ?? [],
      key,
    ];
    updatedOptions = updatedOptions.filter(option => !fieldsToReset.includes(option.key));
  } else {
    const index = updatedOptions.findIndex(option => option.key === key);
    if (index > -1) {
      if (value) {
        updatedOptions.splice(index, 1, { ...updatedOptions[index], key, value });
      } else {
        updatedOptions.splice(index, 1);
      }
    } else {
      updatedOptions.push(createServiceOption({ key, value }));
    }
  }
  return updatedOptions;
};
